import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { Button } from "@whatslly/ui-components/v2/Button/Button";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { modalService } from "@whatslly/ui-components/modals";
import { backgroundClient } from "@whatslly/commons/background/client";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { webScriptClient } from "@whatslly/instant-messaging/webScriptClient";
import { WA_NOTIFICATION_SOUND_MAX_BYTES } from "@whatslly/commons/constants";
import {
  WA_NOTIFICATION_SOUND_ERRORS,
  WA_NOTIFICATION_SOUND_PICK_ERRORS,
} from "@whatslly/commons/waNotificationSoundErrors";
import { useDependencyInjector } from "@whatslly/commons/di";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import styles from "./EditUserModal.module.scss";

export type WaNotificationSoundActions = {
  pickCustomSound: () => Promise<void>;
  clearCustomSound: () => Promise<void>;
  applyPickedFile: (file: File | null) => Promise<void>;
  refreshStatus: () => Promise<void>;
  status: WaNotificationSoundUiStatus;
  useExtensionFilePick: boolean;
};

export type WaNotificationSoundUiStatus = {
  loading: boolean;
  hasCustom: boolean;
  displayName: string | null;
};

export type WaNotificationSoundHostOptions = {
  isDesktop?: boolean;
};

export function useWaNotificationSoundActions(options?: WaNotificationSoundHostOptions): WaNotificationSoundActions {
  const { t } = useTranslation();
  const frontLogger = useDependencyInjector(FrontLogger);
  const logger = useMemo(() => frontLogger.getLogger("edit-user-wa-notification-sound"), [frontLogger]);
  const isDesktop = Boolean(options?.isDesktop);
  const useExtensionFilePick = !isDesktop;
  const [status, setStatus] = useState<WaNotificationSoundUiStatus>({
    loading: true,
    hasCustom: false,
    displayName: null,
  });

  const refreshStatus = useCallback(async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const next = isDesktop
        ? await backgroundClient.send(BgCommands.WA_NOTIFICATION_SOUND_GET_STATUS)
        : await webScriptClient.send(WebScriptCommands.GET_WA_CUSTOM_NOTIFICATION_SOUND_STATUS);
      setStatus({ loading: false, ...next });
    } catch {
      setStatus({ loading: false, hasCustom: false, displayName: null });
    }
  }, [isDesktop]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  const applyPickedFile = useCallback(
    async (file: File | null) => {
      if (!file) {
        return;
      }
      if (file.size > WA_NOTIFICATION_SOUND_MAX_BYTES) {
        modalService.openModal(Toast, { text: t("edit_user.wa_notification_too_large"), type: "error" });
        return;
      }
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
      });
      const comma = dataUrl.indexOf(",");
      const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
      const mimeType = file.type || "audio/mpeg";
      try {
        const uploadResult = await webScriptClient.send(WebScriptCommands.SET_WA_CUSTOM_NOTIFICATION_SOUND, {
          base64,
          mimeType,
          fileName: file.name,
          payloadByteLength: file.size,
        });
        if (uploadResult && !uploadResult.success) {
          if (uploadResult.error === WA_NOTIFICATION_SOUND_ERRORS.TOO_LARGE) {
            modalService.openModal(Toast, { text: t("edit_user.wa_notification_too_large"), type: "error" });
            return;
          }
          if (uploadResult.error === WA_NOTIFICATION_SOUND_ERRORS.INVALID_FORMAT) {
            modalService.openModal(Toast, { text: t("edit_user.wa_notification_invalid_format"), type: "error" });
            return;
          }
          modalService.openModal(Toast, { text: t("edit_user.wa_notification_sync_failed"), type: "error" });
          return;
        }
        await refreshStatus();
        modalService.openModal(Toast, { text: t("edit_user.wa_notification_saved"), type: "regular" });
      } catch {
        modalService.openModal(Toast, { text: t("edit_user.wa_notification_sync_failed"), type: "error" });
      }
    },
    [t, refreshStatus]
  );

  const pickCustomSound = useCallback(async () => {
    if (isDesktop) {
      try {
        const res = await backgroundClient.send(BgCommands.WA_NOTIFICATION_SOUND_PICK);
        if (res.success) {
          await refreshStatus();
          modalService.openModal(Toast, { text: t("edit_user.wa_notification_saved"), type: "regular" });
          return;
        }
        if (res.error === WA_NOTIFICATION_SOUND_ERRORS.TOO_LARGE) {
          modalService.openModal(Toast, { text: t("edit_user.wa_notification_too_large"), type: "error" });
          return;
        }
        if (res.error === WA_NOTIFICATION_SOUND_ERRORS.INVALID_FORMAT) {
          modalService.openModal(Toast, { text: t("edit_user.wa_notification_invalid_format"), type: "error" });
          return;
        }
        if (res.error === WA_NOTIFICATION_SOUND_PICK_ERRORS.CANCELLED) {
          return;
        }
        modalService.openModal(Toast, { text: t("edit_user.wa_notification_sync_failed"), type: "error" });
      } catch {
        modalService.openModal(Toast, { text: t("edit_user.wa_notification_sync_failed"), type: "error" });
      }
      return;
    }

    // Extension / web: native file dialog via `<input type="file">` (see EditUserWaNotificationSoundSection).
  }, [t, refreshStatus, isDesktop]);

  const clearCustomSound = useCallback(async () => {
    if (isDesktop) {
      try {
        await backgroundClient.send(BgCommands.WA_NOTIFICATION_SOUND_CLEAR);
        await refreshStatus();
        modalService.openModal(Toast, { text: t("edit_user.wa_notification_cleared"), type: "regular" });
      } catch (err) {
        logger.warn({ err }, "failed to clear WA notification sound via desktop background");
        modalService.openModal(Toast, { text: t("edit_user.wa_notification_sync_failed"), type: "error" });
      }
      return;
    }

    try {
      await backgroundClient.send(BgCommands.WA_NOTIFICATION_SOUND_CLEAR);
      await refreshStatus();
      modalService.openModal(Toast, { text: t("edit_user.wa_notification_cleared"), type: "regular" });
      return;
    } catch (err) {
      logger.debug({ err }, "WA_NOTIFICATION_SOUND_CLEAR not handled in background; using web-script clear");
    }

    try {
      await webScriptClient.send(WebScriptCommands.SET_WA_CUSTOM_NOTIFICATION_SOUND, { clear: true });
      await refreshStatus();
      modalService.openModal(Toast, { text: t("edit_user.wa_notification_cleared"), type: "regular" });
    } catch (err) {
      logger.warn({ err }, "failed to clear WA notification sound via web-script");
      modalService.openModal(Toast, { text: t("edit_user.wa_notification_sync_failed"), type: "error" });
    }
  }, [t, refreshStatus, isDesktop, logger]);

  return useMemo(
    () => ({
      pickCustomSound,
      clearCustomSound,
      applyPickedFile,
      refreshStatus,
      status,
      useExtensionFilePick,
    }),
    [pickCustomSound, clearCustomSound, applyPickedFile, refreshStatus, status, useExtensionFilePick]
  );
}

export function EditUserWaNotificationSoundSection({ sound }: { sound: WaNotificationSoundActions }) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { status, useExtensionFilePick, pickCustomSound, clearCustomSound, applyPickedFile } = sound;

  const displayValue = status.loading
    ? t("edit_user.wa_notification_loading")
    : status.hasCustom
    ? status.displayName?.trim() || t("edit_user.wa_notification_custom_untitled")
    : t("edit_user.wa_notification_value_default");

  return (
    <div className={classnames(styles.editSection, styles.notificationSoundSection)} data-testid="edit-user-wa-notification-sound">
      <span className={styles.label}>{t("edit_user.wa_notification_section")}</span>
      <div className={styles.notificationSoundRow}>
        <div className={styles.notificationSoundCurrent}>
          <span className={styles.notificationSoundCurrentLabel}>{t("edit_user.wa_notification_current")}</span>
          <span className={styles.notificationSoundFileName} title={!status.loading && status.hasCustom ? displayValue : undefined}>
            {displayValue}
          </span>
        </div>
      </div>
      <div className={styles.notificationSoundActions}>
        {useExtensionFilePick ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className={styles.hiddenFileInput}
              tabIndex={-1}
              accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
              aria-hidden
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                e.target.value = "";
                void applyPickedFile(f);
              }}
            />
            <Button
              type="button"
              secondary
              width="100%"
              paddingInline="large"
              text={t("edit_user.wa_notification_choose_file")}
              onClick={() => fileInputRef.current?.click()}
            />
          </>
        ) : (
          <Button
            type="button"
            secondary
            width="100%"
            paddingInline="large"
            text={t("edit_user.wa_notification_choose_file")}
            onClick={() => void pickCustomSound()}
          />
        )}
        <Button
          type="button"
          link
          width="100%"
          paddingInline="large"
          text={t("edit_user.wa_notification_clear")}
          disabled={status.loading || !status.hasCustom}
          onClick={() => void clearCustomSound()}
        />
      </div>
    </div>
  );
}
